import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, Calendar, CheckCircle2, ChevronDown, ChevronRight,
  Download, Loader2, Plus, Search, Sparkles, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  listCompanies,
  listResolutionTypes,
  multiCompanyBatch,
  downloadMultiZip,
} from "@/lib/api";
import Combobox from "@/components/Combobox";
import SpecificFieldsPanel, { coerceSpecificFieldNumbers } from "@/components/SpecificFieldsPanel";
import { formatIndianDate, isoToDmy, dmyToIso } from "@/lib/date";

const MAX_CO = 10;
const MAX_ITEMS = 5;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Stable client-side IDs for the item rows (avoids React array-index-as-key
// pitfalls when the user adds / removes resolutions out of order).
let _miUidCounter = 0;
const nextMiUid = () => `mi-${++_miUidCounter}-${Date.now()}`;

export default function MultiGenerate() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]); // company ids
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([
    { uid: nextMiUid(), resolution_type_key: "", custom_title: "", custom_context: "", specific_fields: {} },
  ]);
  const [meetingDate, setMeetingDate] = useState(todayISO());
  const [meetingTime, setMeetingTime] = useState("11:00 AM");
  const [meetingPlace, setMeetingPlace] = useState("");
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [overrides, setOverrides] = useState({}); // {company_id: "YYYY-MM-DD"}

  // Run state
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // MultiCompanyBatchResponse
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    Promise.all([listCompanies(), listResolutionTypes()])
      .then(([cRes, tRes]) => {
        setCompanies(cRes.data || []);
        setTypes(tRes.data || []);
      })
      .catch(() => toast.error("Could not load companies / resolution types"))
      .finally(() => setLoadingMeta(false));
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const t of types) {
      const cat = t.category || "Other";
      (out[cat] = out[cat] || []).push(t);
    }
    return out;
  }, [types]);

  const typeOptions = useMemo(
    () =>
      Object.entries(grouped).flatMap(([cat, list]) =>
        list.map((t) => ({ value: t.key, label: t.title, group: cat })),
      ),
    [grouped],
  );

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.cin || "").toLowerCase().includes(q),
    );
  }, [companies, search]);

  const toggleCompany = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_CO) {
        toast.warning(`Max ${MAX_CO} companies per batch`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectAllVisible = () => {
    const visibleIds = filteredCompanies.slice(0, MAX_CO).map((c) => c.id);
    setSelectedIds(visibleIds);
    if (filteredCompanies.length > MAX_CO) {
      toast.message(`Selected first ${MAX_CO} (cap per batch).`);
    }
  };

  const clearAll = () => setSelectedIds([]);

  const addItem = () => {
    if (items.length >= MAX_ITEMS) {
      toast.warning(`Max ${MAX_ITEMS} resolutions per company`);
      return;
    }
    setItems([
      ...items,
      { uid: nextMiUid(), resolution_type_key: "", custom_title: "", custom_context: "", specific_fields: {} },
    ]);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) =>
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  // Reset specific_fields when type changes — the field set is different.
  const updateItemType = (idx, newKey) =>
    setItems(items.map((it, i) => (i === idx
      ? { ...it, resolution_type_key: newKey, specific_fields: {} }
      : it)));

  // Lookup helper — returns the full type metadata for a key (incl. specific_fields).
  const typeByKey = useMemo(() => {
    const out = {};
    for (const t of types) out[t.key] = t;
    return out;
  }, [types]);

  // Members of the FIRST selected company — used by SpecificFieldsPanel to
  // populate the transferor/transferee dropdowns. When multiple companies are
  // selected, we still show the first one's roster (each company's actual
  // members are validated on the backend during save).
  const firstSelectedMembers = useMemo(() => {
    const firstId = selectedIds[0];
    if (!firstId) return [];
    const c = companies.find((x) => x.id === firstId);
    return c?.members || [];
  }, [selectedIds, companies]);

  const canRun =
    !running &&
    selectedIds.length > 0 &&
    items.length > 0 &&
    items.every((it) => it.resolution_type_key) &&
    meetingDate;

  const allResolutionIds = useMemo(
    () =>
      (result?.results || [])
        .flatMap((r) => (r.resolutions || []).map((x) => x.id))
        .filter(Boolean),
    [result],
  );

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await multiCompanyBatch({
        company_ids: selectedIds,
        items: items.map((it) => {
          const rt = typeByKey[it.resolution_type_key];
          const sf = coerceSpecificFieldNumbers(rt?.specific_fields, it.specific_fields);
          return {
            resolution_type_key: it.resolution_type_key,
            custom_title: it.custom_title || undefined,
            custom_context: it.custom_context || undefined,
            is_special_resolution: false,
            include_shareholder_resolution: false,
            specific_fields: sf && Object.keys(sf || {}).length > 0 ? sf : undefined,
          };
        }),
        meeting_date: meetingDate,
        meeting_date_overrides: overrides,
        meeting_time: meetingTime,
        meeting_place: meetingPlace,
        meeting_type: "board",
        include_notice: true,
        include_minutes: true,
        use_style_library: true,
      });
      setResult(res.data);
      toast.success(
        `Drafted & saved ${res.data.total_resolutions} resolutions across ${res.data.succeeded} companies`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!allResolutionIds.length) return;
    setDownloadingZip(true);
    try {
      await downloadMultiZip(allResolutionIds, {
        includeNotice: true,
        includeMinutes: true,
        formats: ["pdf", "docx"],
      });
      toast.success("ZIP downloaded");
    } catch {
      toast.error("ZIP download failed");
    } finally {
      setDownloadingZip(false);
    }
  };

  if (loadingMeta) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="multi-generate-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700 mb-2">
          <Sparkles className="w-3.5 h-3.5" /> Multi-Company Mode
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Generate Quarterly Resolutions in Bulk
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Pick up to {MAX_CO} companies and up to {MAX_ITEMS} resolution types. We draft AI bodies, notices and minutes, and save each company's meeting separately to the vault — one click.
        </p>
      </div>

      {/* === STEP 1: Companies === */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-700" /> 1. Select Companies
            <span className="text-xs font-normal text-slate-500">
              {selectedIds.length} / {MAX_CO} selected
            </span>
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAllVisible}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50"
              data-testid="multi-select-all"
            >
              Select All
            </button>
            <button
              onClick={clearAll}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50"
              data-testid="multi-clear-all"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company name or CIN…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="multi-company-search"
          />
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto border border-slate-200 rounded-lg p-2"
          data-testid="multi-company-list"
        >
          {filteredCompanies.length === 0 && (
            <div className="text-sm text-slate-500 p-4 col-span-full text-center">
              No companies match. <Link to="/companies/new" className="text-blue-700 underline">Add one</Link>.
            </div>
          )}
          {filteredCompanies.map((c) => {
            const checked = selectedIds.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                  checked
                    ? "border-blue-500 bg-blue-50/50"
                    : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`multi-company-row-${c.id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCompany(c.id)}
                  className="w-4 h-4 accent-blue-700"
                  data-testid={`multi-company-checkbox-${c.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {c.name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.company_type || c.type || "—"} · {c.cin || "—"} · {(c.directors || []).length} directors
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* === STEP 2: Resolution Types === */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-700" /> 2. Resolution Types
            <span className="text-xs font-normal text-slate-500">
              {items.length} / {MAX_ITEMS}
            </span>
          </h2>
          <button
            onClick={addItem}
            disabled={items.length >= MAX_ITEMS}
            className="text-xs px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="multi-add-item"
          >
            <Plus className="w-3.5 h-3.5" /> Add Resolution
          </button>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => {
            const rt = typeByKey[it.resolution_type_key];
            return (
            <div
              key={it.uid}
              className="border border-slate-200 rounded-lg p-3"
              data-testid={`multi-item-${idx}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Combobox
                    options={typeOptions}
                    value={it.resolution_type_key}
                    onChange={(v) => updateItemType(idx, v)}
                    placeholder="Pick a resolution type…"
                    testId={`multi-item-${idx}-type`}
                  />
                  {it.resolution_type_key && rt?.specific_fields?.length > 0 && (
                    <div className="mt-2">
                      <SpecificFieldsPanel
                        fields={rt.specific_fields}
                        values={it.specific_fields}
                        onChange={(sf) => updateItem(idx, { specific_fields: sf })}
                        title={rt.title}
                        testIdPrefix={`multi-specific-${idx}`}
                        members={firstSelectedMembers}
                      />
                    </div>
                  )}
                  {it.resolution_type_key && (
                    <textarea
                      value={it.custom_context}
                      onChange={(e) =>
                        updateItem(idx, { custom_context: e.target.value })
                      }
                      placeholder={
                        rt?.specific_fields?.length > 0
                          ? "Optional — any extra background (particulars already captured above)"
                          : "Optional: extra context for the AI (e.g. amount, vendor, period)…"
                      }
                      rows={2}
                      className="mt-2 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
                      data-testid={`multi-item-${idx}-context`}
                    />
                  )}
                </div>
                {items.length > 1 && (
                  <button
                    onClick={() => removeItem(idx)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    data-testid={`multi-item-${idx}-remove`}
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* === STEP 3: Meeting Details === */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-blue-700" /> 3. Meeting Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-600 uppercase tracking-wide">Meeting Date (default for all)</label>
            <input
              value={isoToDmy(meetingDate)}
              onChange={(e) => setMeetingDate(e.target.value)}
              onBlur={(e) => {
                const iso = dmyToIso(e.target.value);
                if (iso || !e.target.value) setMeetingDate(iso || "");
              }}
              placeholder="DD-MM-YYYY"
              inputMode="numeric"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="multi-meeting-date"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 uppercase tracking-wide">Meeting Time</label>
            <input
              value={meetingTime}
              onChange={(e) => setMeetingTime(e.target.value)}
              placeholder="11:00 AM"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="multi-meeting-time"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 uppercase tracking-wide">Meeting Place</label>
            <input
              value={meetingPlace}
              onChange={(e) => setMeetingPlace(e.target.value)}
              placeholder="(blank = each company's registered office)"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="multi-meeting-place"
            />
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <button
              onClick={() => setOverridesOpen((v) => !v)}
              className="text-sm text-slate-700 flex items-center gap-1.5 hover:text-slate-900"
              data-testid="multi-overrides-toggle"
            >
              {overridesOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Customise date per company (optional)
            </button>
            {overridesOpen && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="multi-overrides">
                {selectedIds.map((cid) => {
                  const co = companies.find((x) => x.id === cid);
                  if (!co) return null;
                  return (
                    <div
                      key={cid}
                      className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{co.name}</div>
                      </div>
                      <input
                        value={isoToDmy(overrides[cid] || "")}
                        onChange={(e) =>
                          setOverrides({ ...overrides, [cid]: e.target.value })
                        }
                        onBlur={(e) => {
                          const iso = dmyToIso(e.target.value);
                          if (iso) setOverrides({ ...overrides, [cid]: iso });
                          else if (!e.target.value) {
                            const o = { ...overrides };
                            delete o[cid];
                            setOverrides(o);
                          }
                        }}
                        placeholder={isoToDmy(meetingDate) || "DD-MM-YYYY"}
                        inputMode="numeric"
                        className="px-2 py-1 border border-slate-300 rounded text-sm w-32"
                        data-testid={`multi-override-${cid}`}
                      />
                      {overrides[cid] && (
                        <button
                          onClick={() => {
                            const o = { ...overrides };
                            delete o[cid];
                            setOverrides(o);
                          }}
                          className="text-slate-400 hover:text-slate-700"
                          aria-label="Reset override"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>

      {/* === Action bar === */}
      {!result && (
        <div className="sticky bottom-4 z-10 bg-white border border-slate-200 rounded-2xl p-4 shadow-lg flex items-center justify-between">
          <div className="text-sm text-slate-700">
            <span className="font-semibold">
              {selectedIds.length} companies × {items.filter((i) => i.resolution_type_key).length} resolutions
            </span>
            {selectedIds.length > 0 && items.some((i) => i.resolution_type_key) && (
              <span className="text-slate-500">
                {" "}= {selectedIds.length * items.filter((i) => i.resolution_type_key).length} drafts
              </span>
            )}
          </div>
          <button
            disabled={!canRun}
            onClick={handleRun}
            className="px-5 py-2.5 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="multi-run-cta"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Drafting…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Generate &amp; Save All
              </>
            )}
          </button>
        </div>
      )}

      {/* === Results === */}
      {result && (
        <section
          className="bg-white border border-slate-200 rounded-2xl p-6 mt-2"
          data-testid="multi-results"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Done
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {result.total_resolutions} resolutions saved across{" "}
                {result.succeeded} companies.{" "}
                {result.failed > 0 && (
                  <span className="text-red-700">
                    {result.failed} failed (see below).
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {allResolutionIds.length > 0 && (
                <button
                  onClick={handleDownloadZip}
                  disabled={downloadingZip}
                  className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                  data-testid="multi-download-zip"
                >
                  {downloadingZip ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download All (PDF + DOCX)
                </button>
              )}
              <button
                onClick={() => {
                  setResult(null);
                  setSelectedIds([]);
                  setItems([{ resolution_type_key: "", custom_title: "", custom_context: "", specific_fields: {} }]);
                  setOverrides({});
                }}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
                data-testid="multi-reset"
              >
                New Batch
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {result.results.map((r) => (
              <div
                key={r.company_id}
                className={`border rounded-xl p-4 ${
                  r.error
                    ? "border-red-200 bg-red-50/40"
                    : "border-slate-200"
                }`}
                data-testid={`multi-result-${r.company_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-slate-900">
                      {r.company_name}
                    </div>
                    <div className="text-xs text-slate-500">
                      Meeting on {formatIndianDate(r.meeting_date)}
                    </div>
                  </div>
                  {r.meeting_id && (
                    <Link
                      to={`/meetings/${r.meeting_id}`}
                      className="text-xs text-blue-700 hover:underline"
                      data-testid={`multi-view-meeting-${r.company_id}`}
                    >
                      View meeting →
                    </Link>
                  )}
                </div>
                {r.error ? (
                  <div className="text-sm text-red-700">{r.error}</div>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {r.resolutions.map((res) => (
                      <li
                        key={res.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-slate-700">
                          <span className="font-mono text-xs text-slate-500 mr-2">
                            {res.resolution_number}
                          </span>
                          {res.resolution_title}
                        </span>
                        <Link
                          to={`/resolutions/${res.id}`}
                          className="text-xs text-blue-700 hover:underline"
                          data-testid={`multi-view-res-${res.id}`}
                        >
                          Open
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
